import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { RootState } from '../../../state/state';
import { Status } from '../../../models/status';
import {
  getCancelledFlightCount,
  getChangedFlightCount,
  getFlightCount,
  getKeptFlightCount,
} from '../../flight-availability-service/state/flight-availability-service.selectors';
import { getReshopFare, getReshopFareComplete } from '../../pricing-api-service/state/pricing-api.actions';
import { ReservationServiceSelectors } from '../../reservation-service/state/reservation-service.selectors';
import { ItineraryService } from '../itinerary.service';
import {
  processUpdateItineraryAndStorePqrComplete,
  refreshFareAndProcessUpdateItineraryForVoluntaryChange,
  reissueComplete,
  reissueMetricsLogged,
  revalComplete,
  revalFailureLogged,
  submitIrropReissue,
  submitReval,
  submitRevalForIrropReissue,
  submitRevalForScheduleChangeReissue,
  submitScheduleChangeReissue,
  submitScheduleChangeReissueAcceptFlights,
  submitSdcReval,
} from './itinerary.actions';
import { ItinerarySelectors } from './itinerary.selectors';
import { VoluntaryChangeStore } from '../../../features/voluntary-change/stores/voluntary-change.store';

@Injectable()
export class ItineraryEffects {
  private readonly actions$ = inject(Actions);
  private readonly itineraryService = inject(ItineraryService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly voluntaryChangeStore = inject(VoluntaryChangeStore);

  refreshFareAndProcessUpdateItineraryForVoluntaryChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshFareAndProcessUpdateItineraryForVoluntaryChange),
      switchMap((action) => {
        // Get the fare summarize request from the VC store for the latest slice
        const fareSummarizeRequest = this.voluntaryChangeStore.getFareSummarizeRequestForCurrentSlice();

        // Dispatch getReshopFare action and wait for completion
        this.store.dispatch(getReshopFare({ request: fareSummarizeRequest, background: false }));
        return this.actions$.pipe(
          ofType(getReshopFareComplete),
          filter((completedAction) => {
            // Only proceed if this completion matches our request
            return (
              completedAction.request.sessionId === fareSummarizeRequest.sessionId &&
              completedAction.request.solutionSetId === fareSummarizeRequest.solutionSetId &&
              completedAction.request.solutionId === fareSummarizeRequest.solutionId &&
              completedAction.request.recordLocator === fareSummarizeRequest.recordLocator
            );
          }),
          take(1),
          mergeMap(() => {
            const updateItinRequest = this.voluntaryChangeStore.buildUpdateItineraryAndStorePqrRequest();

            // After reshop fare completes, make the update itinerary API call
            return this.itineraryService.updateItineraryAndStorePqr(updateItinRequest).pipe(
              map((response) => {
                const status = response?.problemDetails ? Status.FAILED : Status.STABLE;
                return processUpdateItineraryAndStorePqrComplete({ updateItineraryStatus: status, response });
              }),
              catchError(() => {
                return of(processUpdateItineraryAndStorePqrComplete({ updateItineraryStatus: Status.FAILED, response: null }));
              })
            );
          })
        );
      })
    )
  );

  /**
   * Effect for handling a reval when the guest accepts changes
   */
  submitReval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitReval),
      withLatestFrom(this.store.select(ItinerarySelectors.getRevalRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndRevalidateTicket(request).pipe(map((result) => revalComplete(result)));
      })
    )
  );

  /**
   * Effect for handling an SDC reval
   */
  submitSdcReval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitSdcReval),
      withLatestFrom(this.store.select(ItinerarySelectors.getSdcRevalRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndRevalidateTicket(request).pipe(map((result) => revalComplete(result)));
      })
    )
  );

  /**
   * Effect for handling a reval when the guest is eligible for reval instead of schedule change reissue
   */
  submitRevalForScheduleChangeReissue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitRevalForScheduleChangeReissue),
      withLatestFrom(this.store.select(ItinerarySelectors.getScheduleChangeReissueNewFlightsRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndRevalidateTicket(request).pipe(map((result) => revalComplete(result)));
      })
    )
  );

  /**
   * Effect for handling a reval when the guest is eligible for reval instead of schedule change reissue
   */
  submitRevalForIrropReissue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitRevalForIrropReissue),
      withLatestFrom(this.store.select(ItinerarySelectors.getIrropReissueNewFlightsRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndRevalidateTicket(request).pipe(map((result) => revalComplete(result)));
      })
    )
  );

  /**
   * Effect for handling a schedule change reissue when the guest accepts the flights as is
   */
  submitScheduleChangeReissueAcceptFlights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitScheduleChangeReissueAcceptFlights),
      withLatestFrom(this.store.select(ItinerarySelectors.getScheduleReissueRequestAcceptFlights)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndReissueTicket(request).pipe(map((result) => reissueComplete(result)));
      })
    )
  );

  /**
   * Effect for handling a schedule change reissue where the guest changes the flights
   */
  submitScheduleChangeReissue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitScheduleChangeReissue),
      withLatestFrom(this.store.select(ItinerarySelectors.getScheduleChangeReissueNewFlightsRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndReissueTicket(request).pipe(map((result) => reissueComplete(result)));
      })
    )
  );

  /**
   * Effect for handling an IRROP reissue where the guest changes the flights
   */
  submitIrropReissue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitIrropReissue),
      withLatestFrom(this.store.select(ItinerarySelectors.getIrropReissueNewFlightsRequest)),
      mergeMap(([action, request]) => {
        return this.itineraryService.changeItineraryAndReissueTicket(request).pipe(map((result) => reissueComplete(result)));
      })
    )
  );

  /**
   * Effect for handling a reval failure for either schedule change or irrop
   */
  logRevalFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(revalComplete),
      withLatestFrom(this.store.select(ReservationServiceSelectors.getSelectedInvoluntaryChangeOption)),
      mergeMap(([action, involType]) => {
        return of(revalFailureLogged());
      })
    )
  );

  logReissueMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reissueComplete),
      withLatestFrom(
        this.store.pipe(select(getChangedFlightCount)),
        this.store.pipe(select(getKeptFlightCount)),
        this.store.pipe(select(getCancelledFlightCount)),
        this.store.pipe(select(getFlightCount)),
        this.store.pipe(select(ReservationServiceSelectors.getSelectedInvoluntaryChangeOption))
      ),
      mergeMap(([action, changedFlightCount, keptFlightCount, cancelledFlightCount, totalFlights, ticketChangeType]) => {
        return of(reissueMetricsLogged());
      })
    )
  );
}
